import { SegmentPill } from 'components/ui/overlay-motion';
import { ToggleGroupItem } from 'components/ui/toggle-group';
import { Tooltip } from 'components/ui/tooltip';
import { LayoutGroup } from 'motion/react';
import { Fragment, type ReactNode, useId } from 'react';
import { cn } from 'utils/cn';
import { SingleChoiceGroup } from '../SingleChoiceGroup';

type Props<Value extends string> = {
    value: Value;
    /** An option with an `off` reason is shown, stays in reach and cannot be chosen; the reason says why. */
    options: { value: Value; label: ReactNode; off?: string }[];
    onChange: (value: Value) => void;
    /** The id of the text that names the group. */
    labelledBy: string;
    /** The options share the whole width between them. */
    fill?: boolean;
    className?: string;
};

/**
 * One choice out of a few, as the segments of one control. One pill sits under the labels and slides to the chosen
 * segment, as the tabs' indicator does.
 */
export function Segmented<Value extends string>({
    value,
    options,
    onChange,
    labelledBy,
    fill = false,
    className,
}: Props<Value>) {
    const offId = useId();
    const pillGroup = useId();
    return (
        <LayoutGroup id={pillGroup}>
            <SingleChoiceGroup
                aria-labelledby={labelledBy}
                value={value}
                onChange={(next) => !options.find((option) => option.value === next)?.off && onChange(next)}
                spacing={0.5}
                // `isolate` keeps the pill above the track and under every label. A focused segment stays at `z-auto`:
                // raised, it would lift its pill over the labels the pill slides across.
                className={cn('bg-segmented isolate rounded-[9px] p-[3px]', fill && 'w-full', className)}
            >
                {options.map((option, index) => {
                    // Off, it is not disabled: a disabled toggle leaves the group's arrow keys, and its reason with it.
                    const item = (
                        <ToggleGroupItem
                            key={option.value}
                            value={option.value}
                            aria-disabled={option.off ? true : undefined}
                            aria-describedby={option.off ? `${offId}${index}` : undefined}
                            className={cn(
                                'text-foreground-secondary data-pressed:text-foreground text-meta relative h-[30px] rounded-sm px-3 font-medium hover:bg-transparent focus:z-auto focus-visible:z-auto aria-disabled:cursor-default aria-disabled:opacity-50 aria-pressed:bg-transparent data-pressed:font-semibold',
                                fill && 'min-w-0 flex-1 px-1.5'
                            )}
                        >
                            {option.value === value ? <SegmentPill className="bg-card" /> : null}
                            {option.label}
                        </ToggleGroupItem>
                    );
                    return option.off ? (
                        <Fragment key={option.value}>
                            <Tooltip content={option.off}>{item}</Tooltip>
                            <span id={`${offId}${index}`} hidden>
                                {option.off}
                            </span>
                        </Fragment>
                    ) : (
                        item
                    );
                })}
            </SingleChoiceGroup>
        </LayoutGroup>
    );
}
